<template>
  <article class="notifications-item" v-on="$listeners">
    <a
      :href="notification.target_url"
      class="notifications-item__link"
      data-test-id="notifications-item-link"
      rel="noopener"
      @click="onClick"
    >
      <img
        v-if="!fallbackVisible"
        v-lazy="{ src: notification.image_url }"
        class="notifications-item__image"
        alt=""
      >
      <ELogo v-else class="notifications-item__image" compact />
      <p class="notifications-item__text" data-test-id="notifications-item-text">
        <span v-safe-html="notificationText" class="notifications-item__content" />
        <time
          v-if="dateVisible"
          :datetime="notification.publish_date"
          class="notifications-item__date"
          data-test-id="notifications-item-date"
        >
          {{ formattedDate }}
        </time>
      </p>
    </a>
  </article>
</template>

<script>
import { sendEvent, sendCoreAnalyticsEvent } from '~~/common/js/client/analytics';
import { localiseTimeFromNow, localiseDate } from '~~/common/js/universal/datetime';
import ELogo from '~~/common/vue/components/logo';

export default {
  name: 'ENotificationItem',
  components: {
    ELogo,
  },
  props: {
    locale: {
      type: String,
      required: true,
    },
    notification: {
      type: Object,
      required: true,
    },
    eventOrigin: {
      type: String,
      required: true,
    },
    dateVisible: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      fallbackVisible: false,
    };
  },
  computed: {
    formattedDate() {
      return this.dateVisible ?
        this.formatDate(new Date(this.notification.publish_date)) :
        '';
    },
    notificationText() {
      return this.notification.text.replace(/<(?!\/?strong)[^>]+>/g, '');
    },
  },
  beforeMount() {
    this.handler = ({ src }) => {
      if (src === this.notification.image_url) {
        this.fallbackVisible = true;
      }
    };
    this.$Lazyload.$on('error', this.handler);
  },
  beforeDestroy() {
    this.$Lazyload.$off('error', this.handler);
  },
  methods: {
    onClick() {
      sendEvent('all_notification_click', {
        origin: this.eventOrigin,
        level: this.notification.level,
        trackingname: this.notification.tracking_name,
      });
      sendCoreAnalyticsEvent('all_notification_click', {
        origin: this.eventOrigin,
        level: this.notification.level,
        trackingname: this.notification.tracking_name,
      });
    },
    formatDate(date) {
      return localiseTimeFromNow(this.locale, date) ??
        localiseDate(this.locale, date, { month: 'short' }) ??
        '';
    },
  },
};
</script>

<style lang="scss" scoped>
$image-size: 90px;

.notifications-item {
  width: 400px;
  font-size: $font-m;
}

.notifications-item__link {
  display: flex;
  position: relative;
  padding: $space-m;
  transition: background-color $timing-2s $easing;
  background-color: var(--color-background-elevation-1);

  &,
  &:visited {
    color: var(--color-text-primary);
  }

  &:hover,
  &:focus {
    background-color: var(--color-highlight-primary);
  }
}

.notifications-item__image {
  height: $image-size;
  width: $image-size;
  min-width: $image-size;
  background-size: contain;
  background-color: var(--color-background-secondary);
  border-radius: $border-radius-l;
}

.notifications-item__image::v-deep .logo__icon {
  width: 100%;
}

.notifications-item__text {
  display: block;
  padding: $space-1s $space-m 0 $space-m;
  margin: 0;
  line-height: $line-height-s;
}

.notifications-item__content {
  @include line-clamp(3);
}

.notifications-item__date {
  display: block;
  font-size: $font-s;
  color: var(--color-text-secondary);
  margin-top: $space-2s;
}
</style>
